import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type ViewMode = 'customer' | 'electrician';

const STORAGE_KEY = 'rsc-dash-view-mode';
const DEFAULT_PIN = '1234';

export interface ViewModeContextValue {
  mode: ViewMode;
  isElectrician: boolean;
  setMode: (mode: ViewMode, persist?: boolean) => void;
  tryUnlock: (pin: string, persist?: boolean) => boolean;
  resetToCustomer: () => void;
}

const ViewModeContext = createContext<ViewModeContextValue | null>(null);

function readInitialMode(): ViewMode {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'electrician' || stored === 'customer') return stored;
  } catch {
    // ignore storage access errors (SSR, private mode, etc.)
  }
  return 'customer';
}

export interface ViewModeProviderProps {
  children: ReactNode;
  pin?: string;
}

export function ViewModeProvider({
  children,
  pin = DEFAULT_PIN,
}: ViewModeProviderProps): JSX.Element {
  const [mode, setModeState] = useState<ViewMode>(() => readInitialMode());

  const setMode = useCallback((next: ViewMode, persist = true) => {
    setModeState(next);
    if (persist) {
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // ignore
      }
    }
  }, []);

  const tryUnlock = useCallback(
    (candidate: string, persist = true): boolean => {
      if (candidate === pin) {
        setMode('electrician', persist);
        return true;
      }
      return false;
    },
    [pin, setMode],
  );

  const resetToCustomer = useCallback(() => {
    setMode('customer', true);
  }, [setMode]);

  useEffect(() => {
    if (mode === 'electrician') {
      document.documentElement.setAttribute('data-view-mode', 'electrician');
    } else {
      document.documentElement.setAttribute('data-view-mode', 'customer');
    }
  }, [mode]);

  const value = useMemo<ViewModeContextValue>(
    () => ({
      mode,
      isElectrician: mode === 'electrician',
      setMode,
      tryUnlock,
      resetToCustomer,
    }),
    [mode, setMode, tryUnlock, resetToCustomer],
  );

  return <ViewModeContext.Provider value={value}>{children}</ViewModeContext.Provider>;
}

export function useViewMode(): ViewModeContextValue {
  const ctx = useContext(ViewModeContext);
  if (!ctx) {
    throw new Error('useViewMode must be used within a ViewModeProvider');
  }
  return ctx;
}
